// src/pages/Admin/TableauDeBordAdherents/TableauDeBordAdherentsPage.jsx

import React, { useMemo, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../hooks/useScollToTop";
import ScrollIndicator from "../../Home/ScrollIndicator";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

import AdminDashboardAccessDenied from "./components/AdminDashboardAccessDenied";
import AdminDashboardHero from "./components/AdminDashboardHero";
import AdminDashboardQuickNav from "./components/AdminDashboardQuickNav";
import AdminMobileSection from "./components/AdminMobileSection";

import {
  ADMIN_ADHERENTS_SECTIONS,
  buildDefaultOpenSections,
} from "./data/adminAdherentsSections";

import { useAdminDashboardAccess } from "./hooks/useAdminDashboardAccess";

import {
  FaChartBar,
} from "react-icons/fa";

/**
 * Composants fonctionnels repris de la version Dashboard desktop.
 */
import StatisticsCards from "./../../../dashboard/pages/admin/DashboardAdherents/components/StatisticsCards";
import VisitesLineChart from "./../../../dashboard/pages/admin/DashboardAdherents/components/VisitesLineChart";
import AdherentStatusBreakdownCard from "./../../../dashboard/pages/admin/DashboardAdherents/components/AdherentStatusBreakdownCard";
import GroupDistributionChart from "./../../../dashboard/pages/admin/DashboardAdherents/components/GroupDistributionChart";
import LatestMembers from "./../../../dashboard/pages/admin/DashboardAdherents/components/LatestMembers";
import CommuneDistributionChart from "./../../../dashboard/pages/admin/DashboardAdherents/components/CommuneDistributionChart";
import CommunityStatisticsTable from "./../../../dashboard/pages/admin/DashboardAdherents/components/CommunityStatisticsTable";
import GenderAgeDistributionChart from "./../../../dashboard/pages/admin/DashboardAdherents/components/GenderAgeDistributionChart";

function renderSectionContent(sectionKey) {
  switch (sectionKey) {
    case "derniersInscrits":
      return <LatestMembers />;

    case "visites":
      return <VisitesLineChart />;

    case "statuts":
      return <AdherentStatusBreakdownCard />;

    case "cours":
      return <GroupDistributionChart />;

    case "communes":
      return <CommuneDistributionChart />;

    case "statsDetaillees":
      return <CommunityStatisticsTable />;

    case "genreAge":
      return <GenderAgeDistributionChart />;

    default:
      return null;
  }
}

export default function Dashboard() {
  useScrollToTop();

  const { isAdmin, roleId } = useAdminDashboardAccess();

  const userRole = roleId;

  const [openSections, setOpenSections] = useState(() =>
    buildDefaultOpenSections()
  );

  const openedCount = useMemo(() => {
    return Object.values(openSections).filter(Boolean).length;
  }, [openSections]);

  const toggleSection = (key) => {
    setOpenSections((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  const openAll = () => {
    setOpenSections(
      ADMIN_ADHERENTS_SECTIONS.reduce((acc, section) => {
        acc[section.key] = true;
        return acc;
      }, {})
    );
  };

  const closeAll = () => {
    setOpenSections(
      ADMIN_ADHERENTS_SECTIONS.reduce((acc, section) => {
        acc[section.key] = false;
        return acc;
      }, {})
    );
  };

  if (!isAdmin) {
    return (
      <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
        <ScrollIndicator />

        <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

        <main className="mx-auto w-full max-w-7xl px-3 pb-24 sm:px-6 lg:px-8">
          <AdminDashboardAccessDenied />
        </main>

        <ToastContainer />
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <ScrollIndicator />

      <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

      <main className="mx-auto w-full max-w-7xl px-3 pb-24 sm:px-6 lg:px-8">
        <HeaderTitle
          role="Administrateur"
          title="Dashboard adhérents"
          subtitle="Affiche les statistiques et les données de tes adhérents."
          icon={FaChartBar}
          align="left"
          className="mb-4"
        />

        <div className="mb-5 sm:mb-7">
          <DashboardNavigation role={userRole} />
        </div>

        <AdminDashboardHero />

        <section className="mt-4 overflow-hidden rounded-[2rem] border border-white/70 bg-white/85 shadow-xl shadow-slate-200/70 backdrop-blur dark:border-slate-800/80 dark:bg-slate-900/70 dark:shadow-black/20 sm:mt-6">
          <div className="border-b border-slate-200/70 bg-white/70 px-3 py-3 dark:border-slate-800 dark:bg-slate-900/60 sm:px-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                  Vue mobile
                </p>

                <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                  {openedCount} section{openedCount > 1 ? "s" : ""} ouverte
                  {openedCount > 1 ? "s" : ""}. Les blocs lourds restent
                  pliables pour garder une page rapide et lisible sur smartphone.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 sm:flex">
                <button
                  type="button"
                  onClick={openAll}
                  className="inline-flex min-h-11 items-center justify-center rounded-2xl bg-[#013E8E] px-4 text-sm font-bold text-white shadow-lg shadow-blue-900/20 transition hover:-translate-y-0.5 hover:bg-indigo-800"
                >
                  Tout ouvrir
                </button>

                <button
                  type="button"
                  onClick={closeAll}
                  className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Tout fermer
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-4 p-3 sm:p-5 lg:p-6">
            <StatisticsCards />

            <AdminDashboardQuickNav
              sections={ADMIN_ADHERENTS_SECTIONS}
              openSections={openSections}
            />

            <div className="space-y-4">
              {ADMIN_ADHERENTS_SECTIONS.map((section) => (
                <AdminMobileSection
                  key={section.key}
                  id={section.id}
                  eyebrow={section.eyebrow}
                  title={section.title}
                  description={section.description}
                  icon={section.icon}
                  isOpen={!!openSections[section.key]}
                  onToggle={() => toggleSection(section.key)}
                >
                  {renderSectionContent(section.key)}
                </AdminMobileSection>
              ))}
            </div>
          </div>
        </section>
      </main>

      <ToastContainer />
    </section>
  );
}